import React, { useEffect, useState, useMemo, useRef } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { api, fileUrl } from "@/lib/api";
import { readCache, writeCache } from "@/lib/cache";
import { Tappable, useLightbox } from "@/context/LightboxContext";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import BottomNav from "@/components/app/BottomNav";
import Logo from "@/components/app/Logo";
import OnboardingTour from "@/components/app/OnboardingTour";
import { Sparkles, X, Heart, RotateCcw, Rewind, Zap, ChevronRight, ChevronsRight, Crown, ShieldOff, Star, Send } from "lucide-react";
import { isAndroidTWA } from "@/lib/twa";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import SafetyModal from "@/components/app/SafetyModal";
import MatchPushPrompt, { shouldShowMatchPushPrompt } from "@/components/app/MatchPushPrompt";
import StoriesStrip from "@/components/app/StoriesStrip";
import {
  ActionBtn,
  SwipeCard,
  EmptyDeck,
  DeckSkeleton,
  MatchModal,
  NoteBeforeMatch,
  DesktopDetailsPane,
  ProfileNudges,
  TourBanner,
} from "./discover/components";

export default function Discover() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [users, setUsers] = useState(() => readCache("spark_deck_cache"));
  const [loading, setLoading] = useState(() => readCache("spark_deck_cache").length === 0);
  const [insight, setInsight] = useState({});
  const [matchModal, setMatchModal] = useState(null);
  // After the first match modal closes, surface a push opt-in prompt — but
  // only if the browser hasn't already answered (granted/denied) and we
  // haven't asked yet on this device. shouldShowMatchPushPrompt encapsulates
  // those checks.
  const [pushPrompt, setPushPrompt] = useState(null);
  const [noteTarget, setNoteTarget] = useState(null);   // {user} when premium user is composing a pre-match note
  const [noteText, setNoteText] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const [safetyTarget, setSafetyTarget] = useState(null); // {user} when safety modal is open from swipe card
  const [msgTarget, setMsgTarget] = useState(null);   // {user} when composing a direct message
  const [msgText, setMsgText] = useState("");
  const [msgBusy, setMsgBusy] = useState(false);
  const [tonight, setTonight] = useState({ count: 0, city: null, has_my_beacon: false });
  const nav = useNavigate();
  const [verifiedOnly, setVerifiedOnly] = useState(() => {
    try { return localStorage.getItem("spark_verified_only") === "1"; } catch { return false; }
  });
  const isPremium = !!(user?.premium_until && new Date(user.premium_until) > new Date());

  const load = async () => {
    if (users.length === 0) setLoading(true);
    try {
      const r = await api.get("/discover", {
        params: verifiedOnly && isPremium ? { verified_only: true } : {},
      });
      setUsers(r.data);
      writeCache("spark_deck_cache", r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/tonight/count")
      .then((r) => setTonight(r.data || {}))
      .catch(() => {});
  }, [verifiedOnly]);

  const onToggleVerified = () => {
    if (!isPremium) {
      nav("/upgrade?feature=verified-only");
      return;
    }
    setVerifiedOnly((v) => {
      const next = !v;
      try {
        localStorage.setItem("spark_verified_only", next ? "1" : "0");
      } catch (_) {
        /* ignore quota errors */
      }
      return next;
    });
  };

  const swipe = async (action, target, opts = {}) => {
    setUsers((u) => u.filter((x) => x.id !== target.id));
    // Fire-and-forget profile-view log so we can power /me/viewers.
    // Server-side dedupes per (viewer, viewee, day) so this is cheap.
    api.post(`/users/${target.id}/view`).catch(() => {});    try {
      const payload = { target_id: target.id, action };
      if (opts.message) payload.message = opts.message;
      const r = await api.post("/swipes", payload);
      if (r.data.matched) {
        setMatchModal({ user: target, matchId: r.data.match_id });
      } else if (opts.message) {
        toast.success("Note sent — they'll see it on your card");
      } else if (action === "super_like") {
        toast.success(`✨ Super liked ${target.name?.split(" ")[0] || "them"}`);
      }
    } catch (e) {
      // Surface the Super Like quota error nicely if backend returns 429.
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 429 && typeof detail === "object" && detail?.code === "super_like_limit") {
        toast.error(detail.message);
        // Put the card back at the top of the deck since the action was rejected.
        setUsers((u) => [target, ...u]);
      } else {
        toast.error("Swipe failed");
      }
    }
  };

  // Super Like — sends the super-like but DOES NOT advance the deck. The
  // user stays on the same card so they can boost, send a note, or keep
  // looking before deciding to skip/pass/like.
  const superLike = async (target) => {
    try {
      const r = await api.post("/swipes", { target_id: target.id, action: "super_like" });
      if (r.data?.matched) {
        setMatchModal({ user: target, matchId: r.data.match_id });
      } else {
        toast.success(`✨ Super sparked ${target.name?.split(" ")[0] || "them"}`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 429 && typeof detail === "object" && detail?.code === "super_like_limit") {
        toast.error(detail.message);
      } else {
        toast.error("Super Spark failed");
      }
    }
  };

  // Local stack of users you've skipped (no like/pass committed to the
  // backend, just removed from the deck). Stored in a ref so rapid
  // skip+rewind clicks don't lose state to React batching.
  const skipStackRef = useRef([]);

  // Neutral forward — moves to the next card without recording a like/pass.
  // The skipped person stays in your future deck refresh so you can revisit
  // them. No backend swipe row is written.
  const skipForward = (target) => {
    if (!target) return;
    skipStackRef.current.push(target);
    setUsers((u) => u.filter((x) => x.id !== target.id));
  };

  const onSparkClick = (target) => {
    // Premium users get the option to attach a note. Free users swipe immediately.
    if (user?.is_premium) {
      setNoteTarget(target);
      setNoteText("");
    } else {
      swipe("like", target);
    }
  };

  const sendNoteAndLike = async () => {
    if (!noteTarget) return;
    setNoteBusy(true);
    const trimmed = noteText.trim().slice(0, 240);
    const target = noteTarget;
    setNoteTarget(null);
    setNoteText("");
    setNoteBusy(false);
    await swipe("like", target, trimmed ? { message: trimmed } : {});
  };

  const sendDirectMessage = async () => {
    const body = msgText.trim();
    if (!body || msgBusy || !msgTarget) return;
    setMsgBusy(true);
    const target = msgTarget;
    try {
      const r = await api.post("/dm/requests", { target_id: target.id, text: body });
      setMsgTarget(null);
      setMsgText("");
      if (r.data.existing) {
        toast.success("Message sent", { action: { label: "Open chat", onClick: () => nav(`/chats/${r.data.match_id}`) } });
      } else {
        toast.success(`Message sent to ${target.name?.split(" ")[0] || "them"} — you'll chat once they reply`);
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "Couldn't send the message";
      if (status === 402) {
        toast.error(detail, { action: { label: "Upgrade", onClick: () => nav("/upgrade") } });
      } else {
        toast.error(detail);
      }
    } finally {
      setMsgBusy(false);
    }
  };

  const rewind = async () => {
    // If the last action was a local Skip, just pop it from the local stack —
    // no Spark+ paywall, no backend call. Skips are free to undo. The ref-based
    // stack means rapid clicks never lose track.
    if (skipStackRef.current.length > 0) {
      const last = skipStackRef.current.pop();
      setUsers((u) => [...u, last]);
      return;
    }
    try {
      const r = await api.post("/swipes/rewind");
      if (r.data.restored_user) {
        setUsers((arr) => [...arr, r.data.restored_user]);
      }
    } catch (e) {
      if (e?.response?.status === 404) toast.info("Nothing to bring back yet");
    }
  };

  const boost = async () => {
    if (!user?.is_premium) {
      nav("/upgrade");
      return;
    }
    try {
      await api.post("/boost");
      toast.success("Boosted! You're on top for 30 minutes.");
    } catch {
      toast.error("Could not boost");
    }
  };

  const getInsight = async (target) => {
    if (insight[target.id]) return;
    try {
      setInsight((s) => ({ ...s, [target.id]: "..." }));
      const r = await api.post("/ai/compatibility", { target_id: target.id });
      setInsight((s) => ({ ...s, [target.id]: r.data.insight }));
    } catch {
      setInsight((s) => ({ ...s, [target.id]: null }));
    }
  };

  const top = users[users.length - 1];

  // Desktop keyboard shortcuts — swipe with arrow keys so the user doesn't
  // have to drag the card on a trackpad. Only active when a card is shown
  // and the user isn't typing in an input or modal.
  React.useEffect(() => {
    const handler = (e) => {
      if (!top || loading) return;
      const target = e.target;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
      // Don't hijack keys when a modal/lightbox/safety sheet is open.
      if (document.querySelector('[data-state="open"]')) return;
      if (noteTarget || safetyTarget) return;

      if (e.key === "ArrowLeft") {
        e.preventDefault();
        swipe("pass", top);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        onSparkClick(top);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        superLike(top);
      } else if (e.key.toLowerCase() === "z") {
        e.preventDefault();
        rewind();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [top, loading, noteTarget, safetyTarget]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen max-w-md mx-auto pb-32 pt-6 px-5 relative lg:max-w-2xl lg:pb-2 lg:pt-2">
      <header className="flex items-center justify-between mb-4 px-1">
        <div>
          <h1 className="font-display text-4xl font-light tracking-tight leading-none">{t("discover.title")}</h1>
          <p className="text-[11px] uppercase tracking-widest text-[#A0A0A8] font-bold mt-1">
            {users.length} {users.length === 1 ? "person" : "people"} nearby
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!user?.is_premium && !isAndroidTWA() && (
            <button
              onClick={() => nav("/upgrade")}
              className="px-2.5 sm:px-3 h-10 rounded-full bg-gradient-to-r from-[#B95F73] to-[#8E3A4C] text-[#F0E6D2] flex items-center gap-1.5 text-xs font-bold active:scale-95 shadow-md shadow-[#B95F73]/40"
              data-testid="discover-upgrade-btn"
              title="See Spark+ plans"
            >
              <Zap className="w-3 h-3" fill="currentColor" />
              <span className="hidden xs:inline sm:inline">Spark+</span>
            </button>
          )}
          <button
            onClick={() => nav("/likes")}
            className="px-2.5 sm:px-3 h-10 rounded-full bg-[#C9A961] text-[#16171A] flex items-center gap-1.5 text-xs font-bold active:scale-95"
            data-testid="likes-you-btn"
          >
            <Sparkles className="w-3 h-3" fill="currentColor" />
            <span className="hidden sm:inline">Likes you</span>
            <span className="sm:hidden">Likes</span>
          </button>
          <button
            onClick={() => nav("/top-picks")}
            className="px-3 h-10 rounded-full bg-gradient-to-r from-[#C9A961] to-[#8E6F3A] text-[#16171A] flex items-center gap-1.5 text-xs font-bold active:scale-95 shadow-md shadow-[#C9A961]/30"
            data-testid="top-picks-btn"
            title={t("discover.ai_picks_title")}
          >
            <Crown className="w-3 h-3" />
            <span className="hidden sm:inline">Top picks</span>
            <span className="sm:hidden">Picks</span>
          </button>
          <button
            onClick={load}
            className="w-10 h-10 rounded-full bg-[#1B1B1E] border border-[#2A2A2F] flex items-center justify-center active:scale-90"
            data-testid="reload-deck"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Verified-only filter pill — Spark+ feature.
          Compact on desktop to keep heart button above the fold. */}
      <button
        onClick={onToggleVerified}
        data-testid="verified-only-toggle"
        className={`mb-3 lg:mb-2 mx-1 px-4 h-10 lg:h-8 rounded-full flex items-center gap-2 text-xs font-bold tracking-widest uppercase active:scale-95 transition border ${
          verifiedOnly && isPremium
            ? "bg-[#8FE0C5]/15 border-[#8FE0C5] text-[#8FE0C5]"
            : "bg-[#1B1B1E] border-[#2A2A2F] text-[#A0A0A8]"
        }`}
      >
        <Sparkles className="w-3 h-3" fill={verifiedOnly && isPremium ? "currentColor" : "none"} />
        Verified only
        {!isPremium && <span className="text-[9px] text-[#C9A961] ml-1">Spark+</span>}
        {verifiedOnly && isPremium && <span className="w-1.5 h-1.5 rounded-full bg-[#8FE0C5] ml-1" />}
      </button>

      {/* Stories strip — recent Moments from people you can see. Visible on
          all screen sizes; on laptop it sits right below the header. */}
      <div className="-mx-5 mb-3">
        <StoriesStrip />
      </div>

      {/* Tonight beacon banner */}
      {(tonight.count > 0 || tonight.has_my_beacon) && (
        <button
          onClick={() => nav("/tonight")}
          className="w-full mb-3 lg:mb-2 rounded-2xl p-3 lg:py-2 flex items-center gap-3 active:scale-[0.99] transition relative overflow-hidden"
          style={{
            background: "linear-gradient(135deg, rgba(201,169,97,0.18) 0%, rgba(185,95,115,0.18) 100%)",
            border: "1px solid rgba(201,169,97,0.35)",
          }}
          data-testid="tonight-banner"
        >
          <div className="w-10 h-10 rounded-full bg-[#C9A961] flex items-center justify-center shrink-0">
            <Zap className="w-4 h-4 text-[#16171A]" fill="currentColor" />
          </div>
          <div className="flex-1 text-left min-w-0">
            <div className="font-display text-lg font-medium tracking-tight text-[#F0E6D2] leading-none">
              {tonight.has_my_beacon && tonight.count === 0
                ? "You're live tonight"
                : tonight.count > 0
                ? `${tonight.count} ${tonight.count === 1 ? "person" : "people"} free ${tonight.city ? `in ${tonight.city}` : "right now"}`
                : "Bored? Light it up."}
            </div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#C9A961] mt-1 flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-[#C9A961] rounded-full animate-pulse" />
              Tonight beacons
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-[#C9A961] shrink-0" />
        </button>
      )}

      <div className="lg:flex lg:gap-6 lg:items-start">
        <div className="lg:flex-shrink-0 lg:w-[360px]">
          {/* Nudges + tour banner are mobile-only — on desktop they pushed the
              deck and action buttons below the fold. */}
          <div className="lg:hidden">
            <ProfileNudges user={user} />
            <TourBanner />
          </div>
          <div className="relative h-[58vh] w-full mx-auto lg:h-[calc(100vh-380px)] lg:min-h-[280px] lg:max-w-none" data-testid="discover-deck">
            {loading && <DeckSkeleton />}
            {!loading && users.length === 0 && (
              <EmptyDeck onRewind={rewind} />
            )}
            <AnimatePresence mode="popLayout">
              {top && (
                <SwipeCard
                  key={top.id}
                  user={top}
                  onSwipe={swipe}
                  onAskInsight={() => getInsight(top)}
                  insight={insight[top.id]}
                  onSafety={(u) => setSafetyTarget(u)}
                />
              )}
            </AnimatePresence>

            {/* Pre-warm the next two photos so a fast skip/rewind always has
                the right image cached in the browser. Rendered with zero
                width so they're invisible but the browser still downloads. */}
            {users.slice(-3, -1).map((u) => {
              const ph = u.photos?.[0]?.storage_path;
              if (!ph) return null;
              return (
                <img
                  key={`prefetch-${u.id}`}
                  src={fileUrl(ph, 1080)}
                  alt=""
                  width="0"
                  height="0"
                  aria-hidden="true"
                  style={{ position: "absolute", width: 0, height: 0, opacity: 0, pointerEvents: "none" }}
                />
              );
            })}
          </div>

          {top && !loading && (
            <div className="flex items-center justify-center gap-4 mt-6 lg:mt-2 lg:gap-3">
              <ActionBtn color="#25252A" textColor="#0E0F12" onClick={rewind} testid="rewind-btn" small tooltip="Rewind last swipe" label="Rewind">
                <Rewind className="w-5 h-5" strokeWidth={2.5} />
              </ActionBtn>
              <ActionBtn
                color="#D14545"
                textColor="white"
                onClick={() => swipe("pass", top)}
                testid="pass-btn"
                tooltip="Pass — swipe left"
                label="Pass"
              >
                <X className="w-6 h-6" strokeWidth={3} />
              </ActionBtn>
              <ActionBtn color="#B95F73" textColor="white" onClick={() => onSparkClick(top)} testid="like-btn" big tooltip="Spark — swipe right" label="Spark">
                <Heart className="w-9 h-9" fill="#F0E6D2" />
              </ActionBtn>
              <ActionBtn
                color="#5B8BB4"
                textColor="white"
                onClick={() => { setMsgTarget(top); setMsgText(""); }}
                testid="message-btn"
                small
                tooltip="Message — start a chat right now"
                label="Message"
              >
                <Send className="w-5 h-5" strokeWidth={2.5} />
              </ActionBtn>
              <ActionBtn
                color="#25252A"
                textColor="#F0E6D2"
                onClick={() => skipForward(top)}
                testid="skip-btn"
                small
                tooltip="Skip — just go to the next person (no like, no pass)"
                label="Skip"
              >
                <ChevronsRight className="w-5 h-5" strokeWidth={2.5} />
              </ActionBtn>
              <ActionBtn color="#10B981" textColor="#0E0F12" onClick={() => superLike(top)} testid="super-like-btn" small tooltip="Super Spark — they'll know it's intentional" label="Super">
                <Star className="w-5 h-5" fill="currentColor" strokeWidth={2.5} />
              </ActionBtn>
              <ActionBtn color="#C9A961" textColor="#0E0F12" onClick={boost} testid="boost-btn" small tooltip="Boost — be seen by more people for 30 min" label="Boost">
                <Zap className="w-5 h-5" fill="currentColor" strokeWidth={2.5} />
              </ActionBtn>
            </div>
          )}

          {/* Report / block — page-level, off the photo.
              MOBILE only: under the action buttons.
              On lg+ it's hidden here and rendered inside the desktop details
              pane (right column) so it's always above the fold. */}
          {top && !loading && (
            <div className="flex items-center justify-center mt-4 lg:hidden">
              <button
                onClick={() => setSafetyTarget(top)}
                data-testid="discover-page-safety-btn"
                className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.22em] text-[#6E6A62] hover:text-[#B95F73] transition-colors active:scale-95"
                title="Report or block this person"
              >
                <ShieldOff className="w-3 h-3" strokeWidth={1.8} />
                Report {top.name}
              </button>
            </div>
          )}
        </div>

        {/* Desktop-only expanded details pane — fills the right side so
            the laptop layout has no empty space. Hidden on mobile/tablet. */}
        {top && !loading && (
          <DesktopDetailsPane
            user={top}
            insight={insight[top.id]}
            onAskInsight={() => getInsight(top)}
            onReport={() => setSafetyTarget(top)}
          />
        )}
      </div>

      {matchModal && (
        <MatchModal
          matchUser={matchModal.user}
          me={user}
          onClose={() => {
            const justMatched = matchModal.user;
            setMatchModal(null);
            // Show the push prompt right after the match modal so the moment
            // is fresh in the user's mind. This is the single highest-converting
            // notification opt-in moment in any dating app.
            if (shouldShowMatchPushPrompt()) {
              setPushPrompt({ name: justMatched.name?.split(" ")[0] });
            }
          }}
          onChat={() => {
            const justMatched = matchModal.user;
            const targetMid = matchModal.matchId;
            setMatchModal(null);
            if (shouldShowMatchPushPrompt()) {
              setPushPrompt({ name: justMatched.name?.split(" ")[0], goTo: `/chats/${targetMid}` });
            } else {
              nav(`/chats/${targetMid}`);
            }
          }}
        />
      )}

      {pushPrompt && (
        <MatchPushPrompt
          matchName={pushPrompt.name}
          onClose={() => {
            const goTo = pushPrompt.goTo;
            setPushPrompt(null);
            if (goTo) nav(goTo);
          }}
        />
      )}

      {noteTarget && (
        <NoteBeforeMatch
          target={noteTarget}
          text={noteText}
          onChange={setNoteText}
          busy={noteBusy}
          onClose={() => { setNoteTarget(null); setNoteText(""); }}
          onSkip={async () => { const t = noteTarget; setNoteTarget(null); setNoteText(""); await swipe("like", t); }}
          onSend={sendNoteAndLike}
        />
      )}

      {msgTarget && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="dm-composer-modal">
          <div className="w-full max-w-md bg-[#1B1B1E] border border-[#2A2A2F] rounded-3xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-full overflow-hidden bg-[#25252A] flex items-center justify-center shrink-0">
                {msgTarget.photos?.[0]?.storage_path ? (
                  <img src={fileUrl(msgTarget.photos[0].storage_path, 160)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-display font-black">{msgTarget.name?.[0]}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-xl font-medium truncate">Message {msgTarget.name?.split(" ")[0]}</div>
                <div className="text-[11px] text-[#A0A0A8]">One intro message — chat freely once they reply</div>
              </div>
              <button onClick={() => { setMsgTarget(null); setMsgText(""); }} className="w-8 h-8 rounded-full bg-[#25252A] flex items-center justify-center shrink-0" data-testid="dm-composer-close">
                <X className="w-4 h-4 text-[#A0A0A8]" />
              </button>
            </div>
            <textarea
              value={msgText}
              onChange={(e) => setMsgText(e.target.value)}
              rows={3}
              maxLength={500}
              autoFocus
              placeholder={`Say something ${msgTarget.name?.split(" ")[0]} can't ignore…`}
              className="w-full bg-[#131316] border border-[#2A2A2F] rounded-2xl px-4 py-3 text-[15px] outline-none focus:border-[#5B8BB4] transition resize-none placeholder:text-[#6B6B72]"
              data-testid="dm-composer-input"
            />
            <button
              onClick={sendDirectMessage}
              disabled={!msgText.trim() || msgBusy}
              className="w-full mt-3 py-3 rounded-full bg-[#F0E6D2] text-[#16171A] text-sm font-bold tracking-widest uppercase active:scale-[0.98] transition disabled:opacity-50"
              data-testid="dm-composer-send"
            >
              {msgBusy ? "Sending…" : "Send message"}
            </button>
          </div>
        </div>
      )}

      {safetyTarget && (
        <SafetyModal
          targetId={safetyTarget.id}
          targetName={safetyTarget.name}
          onClose={() => setSafetyTarget(null)}
          onBlocked={() => {
            // Drop the blocked user from the deck immediately
            setUsers((u) => u.filter((x) => x.id !== safetyTarget.id));
            setSafetyTarget(null);
          }}
        />
      )}

      {/* First-time user onboarding (auto-shows once, then writes onboarded_at) */}
      <OnboardingTour user={user} />

      <BottomNav />
    </div>
  );
}

